import * as Sentry from '@sentry/react'
import { environment } from '@/config/environment'

/**
 * Sentry init with a fintech-grade PII scrubber.
 *
 * What we strip from every event:
 *  - `user.email` (keep uid only)
 *  - any `breadcrumb.data.payee`, `category`, `amount` field
 *  - request bodies / URLs containing emails or dollar values
 *  - cookie + auth headers (never logged anyway, but defense-in-depth)
 *
 * Same scrubber shape (rules table) ships to iOS under
 * `Budget/Services/SentryScrubber.swift` so the two clients are
 * audit-equivalent. Reasoning: a Sentry leak that surfaces "user X
 * paid $4 at Starbucks" is a privacy incident; uid-only events are
 * fine.
 */

const PII_FIELD_NAMES = new Set([
  'email',
  'payee',
  'payee_name',
  'amount',
  'memo',
  'note',
  'account_number',
  'mask',
  'access_token',
  'public_token',
])

const MONEY_PATTERN = /\$\d+(?:\.\d{2})?/g
const EMAIL_PATTERN = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g

function scrubString(value: string): string {
  return value.replace(MONEY_PATTERN, '$<redacted>').replace(EMAIL_PATTERN, '<email>')
}

function scrub(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) return value.map(scrub)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (PII_FIELD_NAMES.has(k.toLowerCase())) {
        out[k] = '<redacted>'
      } else {
        out[k] = scrub(v)
      }
    }
    return out
  }
  return value
}

export function initSentry() {
  if (!environment.sentryDsn) return
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.env,
    tracesSampleRate: environment.env === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      if (event.user) {
        // Only the uid survives — email/IP get dropped.
        event.user = { id: event.user.id }
      }
      if (event.request) {
        event.request = scrub(event.request) as typeof event.request
      }
      if (event.extra) {
        event.extra = scrub(event.extra) as typeof event.extra
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          data: b.data ? (scrub(b.data) as typeof b.data) : undefined,
          message: b.message ? scrubString(b.message) : b.message,
        }))
      }
      return event
    },
  })
}
